'use client';

import { AnimatePresence, motion } from 'motion/react';
import { type ComponentType, type FC, useEffect, useMemo, useRef, useState } from 'react';

import { popUp } from '@/components/motion/tokens';
import { initialScrollState } from '@/lib/config/system';
import type { TWithScroll } from '@/lib/interfaces/hoc/withScroll.types';

const withScroll = <P extends object>(WrappedComponent: ComponentType<P & TWithScroll>) => {
  const WithScroll: FC<P> = (props) => {
    const lastScrollYRef = useRef<number>(0);
    const isInitialMount = useRef<boolean>(true);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [container, setContainer] = useState<HTMLElement | null>(null);
    const [currentY, setCurrentY] = useState<number>(0);

    const [scrollState, setScrollState] = useState(initialScrollState);

    const { isVisible, hasScrolled, scrollDirection } = scrollState;

    useEffect(() => {
      // Find app-level scroll container if present
      setContainer(document.getElementById('app-scroll'));
    }, []);

    useEffect(() => {
      let rafId: number | null = null;

      const getScrollY = (): number => {
        if (container) return container.scrollTop;
        return window.scrollY || window.pageYOffset || 0;
      };

      const handleScrollChange = () => {
        if (rafId !== null) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          const newY = getScrollY();
          setCurrentY(newY);
          const lastScrollY = lastScrollYRef.current;

          if (Math.abs(newY - lastScrollY) < 10) return;

          const direction = newY > lastScrollY ? 'down' : 'up';
          lastScrollYRef.current = newY;

          if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

          setScrollState((prev) => {
            let nextVisible = prev.isVisible;
            if (direction === 'up' || newY < 100) {
              nextVisible = true;
            } else if (direction === 'down' && newY > 100) {
              nextVisible = false;
            }
            return {
              ...prev,
              scrollDirection: direction,
              hasScrolled: true,
              isVisible: nextVisible,
            };
          });

          if (direction === 'down' && newY > 100) {
            scrollTimeoutRef.current = setTimeout(() => {
              setScrollState((prev) => ({ ...prev, isVisible: true }));
            }, 1000);
          }
        });
      };

      const target: any = container || window;
      target.addEventListener('scroll', handleScrollChange, { passive: true });

      return () => {
        target.removeEventListener('scroll', handleScrollChange);
        if (rafId !== null) cancelAnimationFrame(rafId);
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      };
    }, [container]);

    const scrollProps: TWithScroll = useMemo(() => {
      return {
        isVisible,
        scrollDirection,
        scrollY: currentY,
      };
    }, [isVisible, scrollDirection, currentY]);

    if (isInitialMount.current || !hasScrolled) {
      isInitialMount.current = false;
      return <WrappedComponent {...props} {...scrollProps} />;
    }

    return (
      <AnimatePresence mode='wait' initial={false}>
        {isVisible && (
          <motion.div
            {...popUp}
            className='fixed inset-x-0 top-0 z-40 w-full transform-gpu bg-background/60 backdrop-blur-xl'
            style={{ willChange: 'transform, opacity' }}
          >
            <WrappedComponent {...props} {...scrollProps} />
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  WithScroll.displayName = `withScroll(${WrappedComponent.displayName || WrappedComponent.name})`;

  return WithScroll;
};

export default withScroll;
